import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FeatureService } from '../../feature-service.service';
import { catchErrorTypedAsync, FunctionsHelperService } from '../../functions-helper.service';
import { randomArray } from '../../../utils/array-utils';

interface IRandomizedState {
    ai_voice: boolean;
    on_boarding_questions: boolean;
    video_type_wizard: boolean;
    video_type_banner: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class RandomizeStateManagerService {
    private _randomizedStatus$ = new BehaviorSubject<IRandomizedState>(null);
    randomizedObject: IRandomizedState = {
        ai_voice: false,
        on_boarding_questions: false,
        video_type_banner: false,
        video_type_wizard: false,
    };

    public randomizedStatus$ = this._randomizedStatus$.asObservable();

    /** 🔢 Versioning — bump this when randomizer logic/data changes */
    private readonly RANDOMIZER_VERSION = '1.0.0';

    constructor(
        private featureService: FeatureService,
        private functionsHelper: FunctionsHelperService
    ) {
        this.getRandomizedDataAsync();
        this.ensureVersionConsistency();
    }

    private ensureVersionConsistency() {
        const storedData = JSON.parse(localStorage.getItem('randomizer')) || null;
        const storedVersion = storedData?.version;

        if (storedVersion !== this.RANDOMIZER_VERSION) {
            console.log(
                `%c[Randomizer] Version mismatch. Expected: ${this.RANDOMIZER_VERSION}, Found: ${storedVersion || 'none'} — Resetting local data.`,
                'color: orange;'
            );
            this.resetRandomizerStorage();
        }
    }

    private resetRandomizerStorage() {
        localStorage.setItem(
            'randomizer',
            JSON.stringify({
                version: this.RANDOMIZER_VERSION,
                data: {},
            })
        );
    }

    async getRandomizedDataAsync() {
        await Promise.allSettled([
            catchErrorTypedAsync(this.getAiVoiceRandomAsync()),
            catchErrorTypedAsync(this.getOnBoardingQuestionsRandom()),
            catchErrorTypedAsync(this.getVideoTypeBannerRandom()),
            catchErrorTypedAsync(this.getVideoTypeWizardRandom()),
        ]);

        this._randomizedStatus$.next(this.randomizedObject);
    }

    private async getAiVoiceRandomAsync(): Promise<void> {
        const hasFeature = await this.featureService.getFeatureAccessValue('randomize_ai_voice');
        this.randomizedObject.ai_voice = hasFeature;
    }

    private async getOnBoardingQuestionsRandom(): Promise<void> {
        const hasFeature = await this.featureService.getFeatureAccessValue('randomize_onboarding_questions');
        this.randomizedObject.on_boarding_questions = hasFeature;
    }

    private async getVideoTypeBannerRandom(): Promise<void> {
        const hasFeature = await this.featureService.getFeatureAccessValue('randomize_videotype_banner');
        this.randomizedObject.video_type_banner = hasFeature;
    }

    private async getVideoTypeWizardRandom(): Promise<void> {
        const hasFeature = await this.featureService.getFeatureAccessValue('randomize_videotype_wizard');
        this.randomizedObject.video_type_wizard = hasFeature;
    }

    getRandomStatusAsync() {
        return this.functionsHelper.getDataAsync(this._randomizedStatus$);
    }

    getAndStoreRandomizedState(key: string, arr: any[]) {
        const stored = JSON.parse(localStorage.getItem('randomizer')) || {};
        const storedVersion = stored.version;
        const isVersionValid = storedVersion === this.RANDOMIZER_VERSION;

        // If no version or mismatched, reset storage
        if (!stored.data || !isVersionValid) {
            this.resetRandomizerStorage();
        }

        const currentData = JSON.parse(localStorage.getItem('randomizer')).data;

        // Return existing data if found
        if (currentData[key]) {
            return currentData[key];
        }

        // Otherwise randomize and store
        const randomized = this.randomState(arr);
        this.storeRandomizeState(key, randomized);

        return randomized;
    }

    getRandomizeState(key: string) {
        const stored = JSON.parse(localStorage.getItem('randomizer')) || {};
        const storedVersion = stored.version;

        if (storedVersion !== this.RANDOMIZER_VERSION) {
            console.warn('[Randomizer] Version mismatch — returning null');
            this.resetRandomizerStorage();
            return null;
        }

        return stored.data?.[key] || null;
    }

    randomState(arr: any[]) {
        return randomArray(arr);
    }

    /** 💾 Store randomized array under a given key */
    storeRandomizeState(key: string, arr: any[]) {
        const stored = JSON.parse(localStorage.getItem('randomizer')) || {
            version: this.RANDOMIZER_VERSION,
            data: {},
        };

        stored.version = this.RANDOMIZER_VERSION;
        stored.data[key] = arr;

        localStorage.setItem('randomizer', JSON.stringify(stored));
    }

    /** 🔀 Shuffle all items except the last one */
    randomExceptLast(arr: any[]) {
        if (arr.length <= 1) return arr;

        const itemsToShuffle = arr.slice(0, -1);
        const lastItem = arr[arr.length - 1];
        const shuffled = randomArray(itemsToShuffle);

        return [...shuffled, lastItem];
    }

    shuffleAll(arr: any[]) {
        if (arr.length <= 1) return arr;
        return randomArray(arr);
    }
}
